import React, { useEffect, useState } from 'react';
import { Share2, Loader2, Trophy, CheckCircle2, Clock, ShieldCheck } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const token = localStorage.getItem('c24_token') || localStorage.getItem('c24_admin_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const AdminReferrals = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/admin/referrals`, authCfg())
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, []);
  return (
    <AdminLayout title="Referral Reports">
      {loading ? (
        <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Stat label="Users with code" value={data.summary.total_users_with_code} icon={Share2} color="#0891B2" bg="#CFFAFE" testid="admin-ref-total" />
            <Stat label="Invites"          value={data.summary.total_invites}         icon={Trophy}  color="#2E7D32" bg="#E8F5E9" testid="admin-ref-invites" />
            <Stat label="Credited"         value={data.summary.credited_invites}      icon={CheckCircle2} color="#D97706" bg="#FEF3C7" testid="admin-ref-credited" />
            <Stat label="Pending"          value={data.summary.pending_invites}       icon={Clock}   color="#6B7280" bg="#F3F4F6" testid="admin-ref-pending" />
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2 bg-white border border-neutral-200 rounded-2xl overflow-hidden" data-testid="admin-ref-table">
              <div className="px-4 py-3 border-b border-neutral-100"><p className="text-sm font-bold">All referrals</p></div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 border-b border-neutral-200 text-left">
                    <tr><Th>User</Th><Th>Code</Th><Th>Referred by</Th><Th>Status</Th></tr>
                  </thead>
                  <tbody>
                    {data.referrals.map((r) => (
                      <tr key={r.user_id} data-testid={`admin-ref-row-${r.user_id}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                        <td className="px-4 py-3">
                          <p className="font-semibold text-neutral-900">{r.user_name || '—'}</p>
                          <p className="text-xs text-neutral-500">{r.user_email}</p>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs bg-neutral-50 rounded">{r.code}</td>
                        <td className="px-4 py-3 text-xs text-neutral-600">
                          {r.referrer_name ? (<><span className="font-semibold">{r.referrer_name}</span><br/><span className="text-neutral-400">{r.referrer_email}</span></>) : <span className="text-neutral-400">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-[11px] font-bold px-2 py-0.5 rounded uppercase ${
                            r.reward_status === 'credited' ? 'bg-[#E8F5E9] text-[#2E7D32]'
                            : r.reward_status === 'pending' ? 'bg-[#FEF3C7] text-[#D97706]'
                            : 'bg-neutral-100 text-neutral-500'
                          }`}>{r.reward_status}</span>
                        </td>
                      </tr>
                    ))}
                    {data.referrals.length === 0 && <tr><td colSpan="4" className="text-center text-neutral-500 py-8">No referrals yet</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white border border-neutral-200 rounded-2xl p-4" data-testid="admin-ref-leaderboard">
              <p className="text-sm font-bold mb-3">Top referrers</p>
              {data.top_referrers.length === 0 ? (
                <p className="text-xs text-neutral-500 py-8 text-center">No referrers yet</p>
              ) : (
                <ol className="space-y-2">
                  {data.top_referrers.map((t, i) => (
                    <li key={t.user_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-neutral-50">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs ${i < 3 ? 'bg-[#FEF3C7] text-[#D97706]' : 'bg-neutral-100 text-neutral-500'}`}>{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate">{t.name || '—'}</p>
                        <p className="text-[11px] text-neutral-500 truncate">{t.email}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-[#2E7D32]">{t.count}</p>
                        <p className="text-[10px] text-neutral-500">{t.credited} paid</p>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
          <p className="mt-3 text-[11px] text-neutral-500 flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[#2E7D32]" /> Reading from shared collection: referrals.</p>
        </>
      )}
    </AdminLayout>
  );
};
const Stat = ({ label, value, icon: Icon, color, bg, testid }) => (
  <div data-testid={testid} className="bg-white border border-neutral-200 rounded-xl p-4">
    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: bg }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <p className="mt-3 text-xs text-neutral-500 font-medium">{label}</p>
    <p className="font-extrabold text-xl mt-0.5">{value}</p>
  </div>
);
const Th = ({ children, className = '' }) => <th className={`px-4 py-2.5 text-xs font-bold text-neutral-500 uppercase tracking-wider ${className}`}>{children}</th>;

export default AdminReferrals;
